import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

function LeadGeneration() {
  const { currentUser, logout, generateLead } = useAuth();
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [emailStatus, setEmailStatus] = useState('');

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    
    // Client-side validation
    if (!customerPhone || !customerEmail) {
      return setError('Customer phone and email are required');
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      return setError('Please enter a valid email address');
    }

    // Validate phone format (basic validation)
    const phoneRegex = /^[+]?[\d\s-()]+$/;
    if (!phoneRegex.test(customerPhone)) {
      return setError('Please enter a valid phone number');
    }

    try {
      setLoading(true);
      const response = await generateLead(customerPhone, customerName, customerEmail);
      
      setSuccess(true);
      setGeneratedLink(response.uniqueLink);
      setEmailStatus(response.emailStatus);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setCustomerPhone('');
        setCustomerName('');
        setCustomerEmail('');
              }, 3000);
    } catch (error) {
      setError(error.message || 'Failed to generate lead. Please try again.');
      console.error('Lead generation error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800">42Cards Solutions DSA Portal</h1>
          <div className="flex items-center">
            <span className="mr-4 text-gray-600">Welcome, {currentUser?.name}</span>
            <button
              onClick={handleLogout}
              className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition duration-200"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center">
              <div className="flex space-x-4">
                <Link
                  to="/dashboard"
                  className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                >
                  Dashboard
                </Link>
                <Link
                  to="/generate-lead"
                  className="text-white px-3 py-2 rounded-md text-sm font-medium bg-gray-900"
                >
                  Generate Lead
                </Link>
                <Link
                  to="/leads"
                  className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                >
                  My Leads
                </Link>
                <Link
                  to="/help"
                  className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                >
                  Help
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main>
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold">CUB NTB Credit Card</h2>
                <p className="text-gray-600 mt-1">Generate lead for DHI CUB Credit Card application</p>
              </div>
              
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}
              
              {success && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                  <p className="font-bold">Lead successfully generated!</p>
                  <p className="mt-1">{emailStatus}</p>
                  <div className="mt-2">
                    <p className="font-medium">Unique referral link:</p>
                    <div className="flex items-center mt-1">
                      <input
                        type="text"
                        value={generatedLink}
                        readOnly
                        className="flex-1 p-2 border border-gray-300 rounded bg-gray-50 text-sm"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(generatedLink);
                          alert('Link copied to clipboard!');
                        }}
                        className="ml-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  <p className="mt-2 text-sm">Share this link with your customer to complete the application.</p>
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="customerPhone">
                    Customer Phone Number *
                  </label>
                  <input
                    id="customerPhone"
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter customer's phone number (e.g., +919876543210)"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Include country code if applicable</p>
                </div>
                
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="customerEmail">
                    Customer Email Address *
                  </label>
                  <input
                    id="customerEmail"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter customer's email address"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Application link will be sent to this email</p>
                </div>
                
                <div className="mb-6">
                  <label className="block text-gray-700 mb-2" htmlFor="customerName">
                    Customer Name (Optional)
                  </label>
                  <input
                    id="customerName"
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter customer's name (optional)"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white p-3 rounded font-medium hover:bg-blue-700 transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? 'Generating Lead...' : 'Generate Lead'}
                </button>
              </form>

              <div className="mt-6 bg-blue-50 border border-blue-200 rounded p-4">
                <h3 className="font-semibold text-blue-800 mb-2">Card Benefits</h3>
                <ul className="text-sm text-blue-900 space-y-1">
                  <li>• 0.75% CASHBACK on eligible card spends</li>
                  <li>• Earn up to 6.75% on your FD</li>
                  <li>• 90% OF FD VALUE as CREDIT limit</li>
                  <li>• No Minimum CIBIL required</li>
                  <li>• ZERO JOINING & ANNUAL FEE</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default LeadGeneration;